import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Typography,
  Box,
  Button,
} from "@mui/material";
import { IoClose } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { dashboardTokens } from "./dashboardStyles";
import { getCaptadorId } from "../../utils/dashboardMetrics";

const FILTERS = [
  { key: "todos", label: "Todos" },
  { key: "semFoto", label: "Sem foto" },
  { key: "semLocalizacao", label: "Sem localização" },
  { key: "semPreco", label: "Sem preço" },
  { key: "integridade", label: "Inconsistência" },
];

/**
 * Modal "Imóveis que precisam de atenção" — junta saúde do estoque
 * (imóveis ativos com dado faltando) e integridade /imoveis x
 * /imoveisPublicos numa única tabela filtrável. Nunca corrige nada
 * automaticamente: cada linha só leva para a tela de edição real.
 */
function AttentionModal({ open, onClose, initialFilter, health, integrity, corretoresMap }) {
  const [filter, setFilter] = useState(initialFilter || "todos");
  const navigate = useNavigate();

  React.useEffect(() => {
    if (open) setFilter(initialFilter || "todos");
  }, [open, initialFilter]);

  const corretorNome = (imovel) => {
    const id = getCaptadorId(imovel);
    const c = id && corretoresMap?.[id];
    return c ? `${c.nome ?? ""} ${c.sobrenome ?? ""}`.trim() : "—";
  };

  const rows = useMemo(() => {
    const list = [];

    health.semFoto.forEach((i) =>
      list.push({
        key: `foto-${i.id}`,
        codigo: i.imovelInfo?.codigo,
        titulo: i.imovelInfo?.titulo,
        problema: "Sem foto",
        problemaKey: "semFoto",
        corretor: corretorNome(i),
        editId: i.id,
      })
    );
    health.semPreco.forEach((i) =>
      list.push({
        key: `preco-${i.id}`,
        codigo: i.imovelInfo?.codigo,
        titulo: i.imovelInfo?.titulo,
        problema: "Sem preço",
        problemaKey: "semPreco",
        corretor: corretorNome(i),
        editId: i.id,
      })
    );
    health.semLocalizacao.forEach((i) =>
      list.push({
        key: `loc-${i.id}`,
        codigo: i.imovelInfo?.codigo,
        titulo: i.imovelInfo?.titulo,
        problema: "Sem cidade/bairro",
        problemaKey: "semLocalizacao",
        corretor: corretorNome(i),
        editId: i.id,
      })
    );
    integrity.orphanPublic.forEach((i) =>
      list.push({
        key: `orphan-${i.id}`,
        codigo: i.codigo,
        titulo: i.titulo,
        problema: "Publicado, mas não existe mais em /imoveis",
        problemaKey: "integridade",
        corretor: "—",
        editId: null,
      })
    );
    integrity.missingPublic.forEach((i) =>
      list.push({
        key: `missing-${i.id}`,
        codigo: i.codigo,
        titulo: i.titulo,
        problema: "Ativo, mas nunca foi publicado",
        problemaKey: "integridade",
        corretor: "—",
        editId: i.id,
      })
    );
    integrity.statusMismatch.forEach((i) =>
      list.push({
        key: `mismatch-${i.id}`,
        codigo: i.codigo,
        titulo: i.titulo,
        problema: `Status divergente (privado: ${i.statusPrivado ? "ativo" : "fechado"}, público: ${i.statusPublico ? "ativo" : "fechado"})`,
        problemaKey: "integridade",
        corretor: "—",
        editId: i.id,
      })
    );

    return list;
  }, [health, integrity]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = filter === "todos" ? rows : rows.filter((r) => r.problemaKey === filter);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 700 }}>
        Imóveis que precisam de atenção
        <IconButton onClick={onClose} size="small">
          <IoClose />
        </IconButton>
      </DialogTitle>
      <Tabs
        value={filter}
        onChange={(_, v) => setFilter(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ px: 2, borderBottom: `1px solid ${dashboardTokens.border}`, minHeight: 40 }}
      >
        {FILTERS.map((f) => (
          <Tab key={f.key} value={f.key} label={f.label} sx={{ minHeight: 40, fontSize: "0.8rem", textTransform: "none" }} />
        ))}
      </Tabs>
      <DialogContent sx={{ p: 0 }}>
        {filtered.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography sx={{ fontSize: "0.85rem", color: dashboardTokens.textSecondary }}>
              Nenhum item nesta categoria.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Código", "Imóvel", "Problema", "Corretor", ""].map((h) => (
                    <TableCell
                      key={h}
                      sx={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: dashboardTokens.textSecondary }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.key}>
                    <TableCell sx={{ fontSize: "0.82rem" }}>{r.codigo || "—"}</TableCell>
                    <TableCell sx={{ fontSize: "0.82rem", maxWidth: 220 }}>
                      <Typography noWrap sx={{ fontSize: "0.82rem" }}>{r.titulo || "—"}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.82rem" }}>{r.problema}</TableCell>
                    <TableCell sx={{ fontSize: "0.82rem" }}>{r.corretor}</TableCell>
                    <TableCell align="right">
                      {r.editId && (
                        <Button
                          size="small"
                          onClick={() => navigate(`/panel/EditImovel/${r.editId}`)}
                          sx={{ textTransform: "none", fontSize: "0.78rem" }}
                        >
                          Editar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AttentionModal;
